import {
  GetTipiDomandaStato,
  useGetFrontTipiDomanda,
  type GetTipiDomandaParams,
} from '@framework/api-client';
import { FormHandler, StyledForm } from '@framework/form';
import { FlexSubmissionRow } from '@framework/frontoffice-submission/src/submission/FlexSubmissionRow';
import { useTranslation } from '@framework/i18n';
import {
  AlignRow,
  ColumnSortDirections,
  getPagination,
  LoaderSpin,
  MessageFilteredList,
  PaginationA11y,
  useBreadcrumbItem,
  useHistory,
  useIdFromUrl,
  usePaginationUrlQueryState,
  VerticalSpace,
  type BrandedPath,
} from '@framework/ui';
import { Card, Col, Empty, Row } from 'antd';
import Title from 'antd/es/typography/Title';
import { isEqual, omit } from 'lodash';
import { useMemo, useState } from 'react';
import { type RouteComponentProps } from 'react-router';
import { down } from 'styled-breakpoints';
import { useBreakpoint } from 'styled-breakpoints/react-styled';
import styled from 'styled-components';
import { FilterDrawerButton } from '../../../ui/drawer/FilterDrawerButton';
import { PageLayout } from '../../../ui/layout/PageLayout';
import { NetworkAlertPage } from '../../errors/network-alert/NetworkAlertPage';
import { LoadingPage } from '../../loading/LoadingPage';
import { InquiryFilterForm } from './filter/InquiryFilterForm';
import { InquiryFilterFormFields } from './filter/InquiryFilterFormFields';
import { InquiryFilterFormFieldsLogic } from './filter/InquiryFilterFormFieldsLogic';
import { InquiryFilterRemoveButton } from './filter/InquiryFilterRemoveButton';
import { useRecipientLockedQuery } from './filter/useRecipientLockedQuery';
import { InquiryListSchema, type InquirySchemaType } from './InquiryListSchema';
import { InquiryLinkButton } from './item/InquiryLinkButton';
import { InquiryListItem } from './item/InquiryListItem';
import { useFetchCategoryLabel } from './useFetchCategoryLabel';

const MaxWidthContainer = styled.div`
  max-width: 100%;
  overflow-x: hidden;
  ${down('sm')} {
    padding: 0 12px;
  }
`;

const PAGE_TITLE_ID = 'inquiry-list-title-focus-id';
const returnFocusToPageTitle = () => {
  const titleElement = document.getElementById(PAGE_TITLE_ID);
  titleElement?.focus();
};

interface Params {
  categoryId: string;
}

export interface IInquiryListPageProps extends RouteComponentProps<Params> {}

const defaultQuery = getPagination({
  page: 0,
  pageSize: 10,
  sort: 'dataCreazione',
  direction: ColumnSortDirections.descend,
});

export function InquiryListPage(props: IInquiryListPageProps) {
  const history = useHistory();
  const { t } = useTranslation();

  const [categoryId] = useIdFromUrl(props, 'categoryId');

  const [openFilterDrawer, setOpenFilterDrawer] = useState(false);
  const isMobile = useBreakpoint(down('sm'));
  const removeAffix = useBreakpoint(down('md'));

  const [query, setQuery] = usePaginationUrlQueryState<GetTipiDomandaParams>({
    history,
    input: defaultQuery,
  });

  const { isRecipientTypeLocked, baseQuery, effectiveQuery } =
    useRecipientLockedQuery(query);

  const { categoryLabel, isLoading: isCategoryLabelLoading } =
    useFetchCategoryLabel(categoryId);

  useBreadcrumbItem({
    path: props.location.pathname as BrandedPath,
    title: categoryLabel || t('inquiry.lista.briciole.domande', 'Domande'),
  });

  const { data, error, isLoading, isFetching } = useGetFrontTipiDomanda(
    {
      ...effectiveQuery,
      osservazioni: false,
      categorie: categoryId ? [categoryId] : effectiveQuery.categorie,
    },
    {
      query: { keepPreviousData: true },
    },
  );

  const { dati: inquiries, meta } = useMemo(() => data ?? {}, [data]);

  // Controllo errore categoria non trovata
  const categoryError =
    categoryId && categoryLabel === null && !isCategoryLabelLoading;

  const isFiltered = useMemo(() => {
    if (isFetching) return false;
    return !isEqual(
      omit(baseQuery, ['pagina', 'ordinamenti', 'limite']),
      omit(defaultQuery, ['pagina', 'ordinamenti', 'limite']),
    );
  }, [baseQuery, isFetching]);

  if (isLoading || isCategoryLabelLoading) return <LoadingPage />;

  if (error || categoryError) {
    return (
      <NetworkAlertPage
        message={t(
          'inquiry.lista.errori.impossibile-caricare-la-sezione',
          "Impossibile caricare l'elenco delle domande",
        )}
        error={error}
      />
    );
  }

  return (
    <FormHandler
      initialValues={effectiveQuery}
      schema={InquiryListSchema}
      // disableReinitialize
      hideVisualFeedback
      onSubmit={async (rawValues: InquirySchemaType) => {
        try {
          const values = await InquiryListSchema.validate(rawValues);
          const formFilter =
            InquiryFilterFormFieldsLogic.showStateSelectFilter()
              ? values
              : {
                  ...omit(values, 'soloAttive'),
                  // Se il checkbox è selezionato, allora il valore è APERTA
                  stato: values.soloAttive
                    ? GetTipiDomandaStato.APERTA
                    : undefined,
                };

          setQuery({
            ...baseQuery,
            ...(isRecipientTypeLocked
              ? omit(formFilter, 'tipoUtenteDestinatario')
              : formFilter),
            // reimposto alla prima pagina con la ricerca
            pagina: 0,
          });
          setOpenFilterDrawer(false);
        } catch (e) {
          console.error(`[Errore] Errore nella ricerca`, e);
        }
      }}
      layout="vertical"
    >
      <PageLayout
        extraButtons={
          isMobile && (
            <FilterDrawerButton
              showFormButtons
              isFiltered={isFiltered}
              open={openFilterDrawer}
              onOpen={() => setOpenFilterDrawer(true)}
              onClose={() => setOpenFilterDrawer(false)}
              onClear={() => {
                setQuery({});
                setOpenFilterDrawer(false);
              }}
            >
              <StyledForm component="div">
                <InquiryFilterFormFields
                  hideCategorySelect={categoryId != null}
                  isRecipientTypeLocked={isRecipientTypeLocked}
                />
              </StyledForm>
            </FilterDrawerButton>
          )
        }
      >
        <FlexSubmissionRow
          mobileRules={{
            unStickyMenu: removeAffix,
          }}
          menu={
            !isMobile && (
              <InquiryFilterForm
                buttons={
                  isFiltered && (
                    <InquiryFilterRemoveButton
                      onClick={() => {
                        setQuery({});
                        returnFocusToPageTitle();
                      }}
                    />
                  )
                }
                hideCategorySelect={categoryId != null}
                isRecipientTypeLocked={isRecipientTypeLocked}
              />
            )
          }
        >
          <LoaderSpin spinning={isFetching} noPadding hasFullWidthChildren>
            <MaxWidthContainer>
              <Title level={1} tabIndex={-1} id={PAGE_TITLE_ID}>
                {categoryLabel ??
                  t('inquiry.tutte-le-domande', 'Tutte le Domande')}
              </Title>
              <MessageFilteredList
                visible={isFiltered}
                items={meta?.totale ?? null}
                message={t(
                  'inquiry.lista.filtri-applicati.domande-mostrate-in-base-ai-filtri-di-ricerca',
                  'domande trovate in base ai filtri di ricerca.',
                )}
                action={
                  <InquiryFilterRemoveButton
                    size="small"
                    onClick={() => {
                      setQuery({});
                      returnFocusToPageTitle();
                    }}
                  />
                }
              />

              <Row gutter={[24, 24]}>
                {inquiries?.length === 0 && (
                  <Col span={24}>
                    <Card>
                      <Empty
                        description={t(
                          'inquiry.lista.vuota.nessuna-domanda-da-mostrare',
                          'Nessuna domanda da mostrare.',
                        )}
                      />
                      <VerticalSpace size={8} />
                      <AlignRow justify="center">
                        <InquiryFilterRemoveButton
                          onClick={() => {
                            setQuery({});
                            returnFocusToPageTitle();
                          }}
                        />
                      </AlignRow>
                    </Card>
                  </Col>
                )}

                {inquiries?.map(inquiry => (
                  <Col xs={24} sm={24} md={12} key={inquiry.id}>
                    <InquiryListItem
                      inquiry={inquiry}
                      actions={
                        <InquiryLinkButton
                          inquiry={inquiry}
                          categoryId={categoryId}
                        />
                      }
                    />
                  </Col>
                ))}
              </Row>
              <div>
                <VerticalSpace />
                <PaginationA11y
                  hideTotal
                  size="default"
                  current={(meta?.pagina ?? 0) + 1}
                  total={meta?.totale ?? 0}
                  pageSize={meta?.limite ?? 10}
                  onChange={(page, pageSize) => {
                    setQuery({
                      ...baseQuery,
                      pagina: page - 1,
                      limite: pageSize,
                    });
                  }}
                />
                {/* accessibilità inserisco lo spazio sotto per non far tagliare l'outline */}
                <VerticalSpace size={6} />
              </div>
            </MaxWidthContainer>
          </LoaderSpin>
        </FlexSubmissionRow>
      </PageLayout>
    </FormHandler>
  );
}
